import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function ContentMarketingPage() {
  const features = [
    'Medical Blog Writing',
    'Patient Education Content',
    'Service Page Optimization',
    'Healthcare SEO Content',
    'Email Newsletter Content',
    'Social Media Content',
    'Video Script Writing',
    'Infographic Creation',
    'Case Study Development',
    'FAQ Content',
    'Condition & Treatment Guides',
    'Content Calendar Management'
  ];

  const contentTypes = [
    {
      icon: 'ri-article-line',
      title: 'Medical Blog Articles',
      description: 'SEO-optimized blog posts that educate patients, build authority, and drive organic traffic to your website.'
    },
    {
      icon: 'ri-book-open-line',
      title: 'Patient Education Guides',
      description: 'Comprehensive guides about conditions, treatments, and procedures that position you as a trusted expert.'
    },
    {
      icon: 'ri-video-line',
      title: 'Video Content Scripts',
      description: 'Engaging video scripts for YouTube, social media, and your website that explain complex topics simply.'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Campaigns',
      description: 'Nurturing email sequences that educate leads, build trust, and convert prospects into patients.'
    },
    {
      icon: 'ri-image-line',
      title: 'Infographics & Visuals',
      description: 'Eye-catching visual content that simplifies medical information and increases social sharing.'
    },
    {
      icon: 'ri-question-answer-line',
      title: 'FAQ Content',
      description: 'Comprehensive FAQ sections that answer patient questions and improve SEO with featured snippets.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-search-eye-line',
      title: 'Improve SEO Rankings',
      description: 'Quality content targeting healthcare keywords helps you rank higher and attract more organic traffic.'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Build Patient Trust',
      description: 'Educational content demonstrates expertise and builds trust before patients ever contact you.'
    },
    {
      icon: 'ri-team-line',
      title: 'Establish Authority',
      description: 'Consistent, high-quality content positions you as the go-to expert in your specialty and market.'
    },
    {
      icon: 'ri-time-line',
      title: 'Long-Term Asset',
      description: 'Content continues generating traffic, leads, and patients months and years after publication.'
    },
    {
      icon: 'ri-share-line',
      title: 'Social Media Fuel',
      description: 'Quality content gives you valuable material to share across all your social media channels.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant',
      description: 'All content follows healthcare marketing guidelines and maintains patient privacy standards.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Content Strategy',
      description: 'Develop a custom content plan targeting your ideal patients, services, and SEO opportunities.'
    },
    {
      step: '02',
      title: 'Topic Research',
      description: 'Identify high-value topics based on patient questions, keyword research, and competitor analysis.'
    },
    {
      step: '03',
      title: 'Content Creation',
      description: 'Expert medical writers create accurate, engaging content that educates and converts readers.'
    },
    {
      step: '04',
      title: 'SEO Optimization',
      description: 'Optimize every piece with proper keywords, headers, meta descriptions, and internal linking.'
    },
    {
      step: '05',
      title: 'Publishing & Promotion',
      description: 'Publish content on your website and promote across email, social media, and other channels.'
    },
    {
      step: '06',
      title: 'Performance Tracking',
      description: 'Monitor traffic, rankings, engagement, and conversions to continuously improve results.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                CONTENT MARKETING
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Build Authority &amp; Attract Patients with Expert Medical Content
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Strategic content marketing that educates patients, improves SEO, and positions your practice as the trusted authority in your specialty.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Content Strategy</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/47b062c4e469845b509cb54ab3530905.jpg"
                  alt="Medical content marketing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Types Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              CONTENT WE CREATE
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Every Type of Content Your Practice Needs
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              From blog posts to video scripts, we create all the content formats that educate patients and drive growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contentTypes.map((type, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${type.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {type.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY CONTENT MARKETING
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Content That Works While You Sleep
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Quality content is a long-term investment that continues attracting and converting patients for years to come.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE SERVICE
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Full-Service Medical Content Marketing
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From strategy to creation to promotion, we handle every aspect of your healthcare content marketing.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Content%20marketing%20strategy%20dashboard%20showing%20editorial%20calendar%20blog%20performance%20analytics%20and%20SEO%20content%20optimization%20for%20healthcare%20practice%20professional%20medical%20content%20management%20interface%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=contentfeatures1&orientation=portrait"
                  alt="Content marketing features"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Strategic Content That Drives Results
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our proven content marketing process ensures every piece serves your business goals and patient needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Build Authority with Strategic Content?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a custom content strategy showing exactly what content will attract your ideal patients and grow your practice.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Content Strategy</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Content Examples</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="content-marketing" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
